import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import documentStorageService from '../../services/documentStorageService';
import { ApiError } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Download
} from 'lucide-react';

const CATEGORY_LABELS = {
  all: 'All Documents',
  license: 'Licenses',
  medical: 'Medical',
  employment: 'Employment',
  training: 'Training',
  background: 'Background Checks',
  compliance: 'Compliance',
  other: 'Other'
};

const CATEGORY_ORDER = [
  'all',
  'license',
  'medical',
  'employment',
  'training',
  'background',
  'compliance',
  'other'
];

const INITIAL_UPLOAD_FORM = {
  documentType: '',
  title: '',
  expirationDate: '',
  issueDate: '',
  notes: '',
  file: null
};

const coerceDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value.toDate === 'function') {
    const parsed = value.toDate();
    return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
  }

  if (typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }

  if (typeof value._seconds === 'number') {
    return new Date(value._seconds * 1000);
  }

  return null;
};

const formatDate = (value, fallback = 'N/A') => {
  const date = coerceDate(value);
  return date ? date.toLocaleDateString() : fallback;
};

const toTitleCase = (value) => {
  if (!value) {
    return '';
  }

  return value
    .toLowerCase()
    .split(' ')
    .map((segment) => (segment ? segment[0].toUpperCase() + segment.slice(1) : segment))
    .join(' ');
};

const getStatusDescriptor = (expirationDate, rawStatus) => {
  const normalizedStatus = typeof rawStatus === 'string' ? rawStatus.trim().toLowerCase() : null;

  if (!expirationDate) {
    if (normalizedStatus) {
      const label = toTitleCase(normalizedStatus.replace(/_/g, ' '));
      const className = normalizedStatus === 'expired'
        ? 'bg-red-100 text-red-800 border-red-300'
        : 'bg-gray-100 text-gray-700 border-gray-300';
      return { text: label, className };
    }

    return {
      text: 'No Expiration',
      className: 'bg-gray-100 text-gray-700 border-gray-300'
    };
  }

  const today = new Date();
  const diffDays = Math.ceil((expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0 || normalizedStatus === 'expired') {
    return { text: 'Expired', className: 'bg-red-100 text-red-800 border-red-300' };
  }
  if (diffDays <= 30) {
    return { text: 'Expiring Soon', className: 'bg-red-100 text-red-800 border-red-300' };
  }
  if (diffDays <= 60) {
    return { text: 'Expires Soon', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
  }

  return { text: 'Valid', className: 'bg-green-100 text-green-800 border-green-300' };
};

const getStatusBucket = (doc) => {
  const statusText = doc.uiStatus?.text || 'Unknown';

  if (statusText === 'Expired') {
    return 'expired';
  }

  if (statusText === 'Expiring Soon' || statusText === 'Expires Soon') {
    return 'expiring';
  }

  if (statusText === 'Valid') {
    return 'valid';
  }

  if (statusText === 'No Expiration') {
    return 'active';
  }

  return 'other';
};

const DriverDocuments = ({ driverId, driverName, driverEmail = '', companyId }) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState(INITIAL_UPLOAD_FORM);
  const [uploadError, setUploadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [lastAutoTitle, setLastAutoTitle] = useState('');
  const [isTitleDirty, setIsTitleDirty] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [viewerDocument, setViewerDocument] = useState(null);
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState(null);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: 'uiTypeLabel', direction: 'asc' });
  const [showStatusInfo, setShowStatusInfo] = useState(false);

  const fileInputRef = useRef(null);
  const isMountedRef = useRef(true);
  const statusInfoContainerRef = useRef(null);

  const { user, userData } = useAuth();

  const resolvedUserContext = useMemo(() => {
    const firebaseUser = user || null;
    const profile = userData || null;

    const firstName = profile?.firstName || firebaseUser?.firstName || null;
    const lastName = profile?.lastName || firebaseUser?.lastName || null;

    const uid = profile?.uid || firebaseUser?.uid || null;
    const email = profile?.email || firebaseUser?.email || null;
    const name = profile?.name
      || firebaseUser?.displayName
      || [firstName, lastName].filter(Boolean).join(' ')
      || null;

    if (!uid && !email && !firstName && !lastName && !name) {
      return null;
    }

    return {
      uid,
      email,
      firstName,
      lastName,
      name
    };
  }, [user, userData]);

  const buildRequestOptions = useCallback(() => {
    const options = { companyId };
    if (resolvedUserContext) {
      options.user = resolvedUserContext;
    }
    return options;
  }, [companyId, resolvedUserContext]);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    if (!showStatusInfo) {
      return;
    }

    const handleClickOutside = (event) => {
      if (
        statusInfoContainerRef.current
        && !statusInfoContainerRef.current.contains(event.target)
      ) {
        setShowStatusInfo(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowStatusInfo(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showStatusInfo, statusInfoContainerRef]);

  const driverDocumentTypes = useMemo(() => {
    const definitions = documentStorageService.getDocumentTypes().driver;
    return Array.isArray(definitions) ? definitions : [];
  }, []);

  const documentTypeLookup = useMemo(() => {
    const lookup = new Map();
    driverDocumentTypes.forEach((type) => {
      lookup.set(type.value, type);
    });
    return lookup;
  }, [driverDocumentTypes]);

  const groupedDocumentTypes = useMemo(() => {
    return driverDocumentTypes.reduce((groups, type) => {
      const category = type.category || 'other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(type);
      return groups;
    }, {});
  }, [driverDocumentTypes]);

  const transformDocument = useCallback((doc) => {
    const expirationDate = coerceDate(doc.expirationDate);
    const uploadDate = coerceDate(doc.updatedAt || doc.createdAt || doc.uploadDate);
    const typeMeta = documentTypeLookup.get(doc.documentType) || null;

    const category = typeMeta?.category || 'other';
    const typeLabel = typeMeta?.label || doc.title || doc.documentType || 'Document';
    const fileSizeLabel = typeof doc.fileSize === 'number'
      ? documentStorageService.formatFileSize(doc.fileSize)
      : (typeof doc.fileSize === 'string' ? doc.fileSize : null);
    const uploaderFullName = doc.uploadedByName
      || doc.uploader?.name
      || [doc.uploader?.firstName, doc.uploader?.lastName].filter(Boolean).join(' ')
      || null;
    const uploaderDisplay = uploaderFullName
      || doc.uploadedBy
      || doc.uploader?.email
      || doc.uploadedByEmail
      || null;

    return {
      ...doc,
      uiCategory: category,
      uiTypeLabel: typeLabel,
      uiExpirationDate: expirationDate,
      uiUploadDate: uploadDate,
      uiStatus: getStatusDescriptor(expirationDate, doc.status),
      uiFileSize: fileSizeLabel,
      uiUploadedBy: uploaderDisplay
    };
  }, [documentTypeLookup]);

  const fetchDocuments = useCallback(async ({ showLoader = true } = {}) => {
    if (!driverId || !companyId) {
      if (isMountedRef.current) {
        setDocuments([]);
        if (showLoader) {
          setLoadError('Driver or company context missing. Select a driver to view documents.');
        }
      }
      return;
    }

    if (showLoader && isMountedRef.current) {
      setIsLoading(true);
      setLoadError(null);
      setActionMessage(null);
    }

    try {
      const response = await documentStorageService.getDriverDocuments(
        driverId,
        {},
        buildRequestOptions()
      );

      const docs = Array.isArray(response?.documents) ? response.documents : [];
      if (isMountedRef.current) {
        setDocuments(docs.map(transformDocument));
        if (showLoader) {
          setActionError(null);
        }
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }

      const friendly = error instanceof ApiError
        ? error.getUserFriendlyMessage()
        : (error?.message || 'Failed to load driver documents.');

      if (showLoader) {
        setLoadError(friendly);
      } else {
        setActionError(friendly);
      }
    } finally {
      if (showLoader && isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [companyId, driverId, transformDocument, buildRequestOptions]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchDocuments();
  }, [fetchDocuments]);

  const documentCategories = useMemo(() => {
    const counts = documents.reduce((acc, doc) => {
      const category = doc.uiCategory || 'other';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});

    return CATEGORY_ORDER.map((id) => ({
      id,
      name: CATEGORY_LABELS[id] || toTitleCase(id),
      count: id === 'all' ? documents.length : (counts[id] || 0)
    }));
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filtered = documents.filter((doc) => {
      const matchesCategory = selectedCategory === 'all' || doc.uiCategory === selectedCategory;
      if (!matchesCategory) {
        return false;
      }

      const statusBucket = getStatusBucket(doc);
      if (selectedStatus !== 'all') {
        if (selectedStatus === 'active' && statusBucket !== 'active' && statusBucket !== 'valid') {
          return false;
        } else if (selectedStatus !== 'active' && statusBucket !== selectedStatus) {
          return false;
        }
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchableFields = [
        doc.title,
        doc.uiTypeLabel,
        doc.documentNumber,
        doc.notes,
        doc.originalFileName,
        doc.fileName
      ];

      return searchableFields.some((field) =>
        typeof field === 'string' && field.toLowerCase().includes(normalizedSearch)
      );
    });

    const getSortValue = (doc, key) => {
      switch (key) {
        case 'uiTypeLabel':
          return (doc.uiTypeLabel || doc.title || '').toLowerCase();
        case 'status': {
          const statusWeight = {
            'Expired': 0,
            'Expiring Soon': 1,
            'Expires Soon': 2,
            'Valid': 3,
            'No Expiration': 4,
            'Unknown': 5
          };
          return statusWeight[doc.uiStatus?.text] ?? 6;
        }
        case 'expiration':
          return doc.uiExpirationDate instanceof Date
            ? doc.uiExpirationDate.getTime()
            : Number.POSITIVE_INFINITY;
        case 'upload':
          return doc.uiUploadDate instanceof Date
            ? doc.uiUploadDate.getTime()
            : Number.NEGATIVE_INFINITY;
        default:
          return (doc.uiTypeLabel || '').toLowerCase();
      }
    };

    const sorted = filtered.slice().sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);

      if (aValue === bValue) {
        return 0;
      }

      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [documents, searchTerm, selectedCategory, selectedStatus, sortConfig]);

  const statusSummary = useMemo(() => {
    const summary = {
      total: documents.length,
      active: 0,
      valid: 0,
      expiring: 0,
      expired: 0
    };

    documents.forEach((doc) => {
      const bucket = getStatusBucket(doc);
      if (bucket === 'active') {
        summary.active += 1;
      } else if (bucket === 'valid') {
        summary.valid += 1;
        summary.active += 1; // Valid documents are also counted as Active
      } else if (bucket === 'expiring') {
        summary.expiring += 1;
      } else if (bucket === 'expired') {
        summary.expired += 1;
      }
    });

    return summary;
  }, [documents]);

  const allExpanded = useMemo(() => (
    filteredDocuments.length > 0 && filteredDocuments.every((doc) => expandedRows.has(doc.id))
  ), [filteredDocuments, expandedRows]);

  const handleFileSelect = (files) => {
    const file = files?.[0];
    if (file) {
      setUploadForm((prev) => ({ ...prev, file }));
      setUploadError(null);
    }
  };

  const handleDocumentTypeChange = (value) => {
    const typeMeta = documentTypeLookup.get(value);
    const typeLabel = typeMeta?.label || '';

    setUploadForm((prev) => {
      const shouldReplaceTitle = !isTitleDirty || !prev.title || prev.title === lastAutoTitle;
      const nextTitle = shouldReplaceTitle ? typeLabel : prev.title;

      if (shouldReplaceTitle) {
        setLastAutoTitle(typeLabel);
        setIsTitleDirty(false);
      }

      return {
        ...prev,
        documentType: value,
        title: nextTitle
      };
    });
  };

  const handleTitleChange = (event) => {
    const nextTitle = event.target.value;
    setUploadForm((prev) => ({
      ...prev,
      title: nextTitle
    }));

    const normalized = nextTitle.trim();
    if (!normalized) {
      setIsTitleDirty(false);
      return;
    }

    if (lastAutoTitle && normalized === lastAutoTitle) {
      setIsTitleDirty(false);
    } else {
      setIsTitleDirty(true);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    if (event.dataTransfer?.files?.length) {
      handleFileSelect(event.dataTransfer.files);
    }
  };

  const resetUploadState = () => {
    setUploadForm(INITIAL_UPLOAD_FORM);
    setUploadError(null);
    setDragOver(false);
    setLastAutoTitle('');
    setIsTitleDirty(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    resetUploadState();
  };

  const handleUpload = async (event) => {
    event.preventDefault();

    if (!driverId || !companyId) {
      setUploadError('Driver and company context are required to upload documents.');
      return;
    }

    if (!uploadForm.file || !uploadForm.documentType || !uploadForm.title.trim()) {
      setUploadError('Please select a document type, provide a title, and choose a file.');
      return;
    }

    try {
      documentStorageService.validateFile(uploadForm.file);
    } catch (validationError) {
      setUploadError(validationError.message);
      return;
    }

    setUploading(true);
    setUploadError(null);
    setActionError(null);
    setActionMessage(null);

    try {
      const payload = {
        title: uploadForm.title.trim(),
        documentType: uploadForm.documentType,
        category: 'driver',
        driverId,
        companyId,
        expirationDate: uploadForm.expirationDate || undefined,
        issueDate: uploadForm.issueDate || undefined,
        notes: uploadForm.notes || ''
      };

      if (driverName) {
        payload.driverName = driverName;
        payload.driverDisplayName = driverName;
      }

      if (driverEmail) {
        payload.driverEmail = driverEmail;
      }

      const response = await documentStorageService.createDocument(
        payload,
        uploadForm.file,
        buildRequestOptions()
      );

      if (response?.document && isMountedRef.current) {
        closeUploadModal();
        await fetchDocuments({ showLoader: false });
        const uploadedLabel = payload.title || payload.documentType || 'Document';
        setActionMessage(`${uploadedLabel} was uploaded successfully.`);
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }

      const friendly = error instanceof ApiError
        ? error.getUserFriendlyMessage()
        : (error?.message || 'Failed to upload document.');
      setUploadError(friendly);
    } finally {
      if (isMountedRef.current) {
        setUploading(false);
      }
    }
  };

  const requestDeleteConfirmation = (doc) => {
    setActionError(null);
    setActionMessage(null);
    setDocumentToDelete(doc);
  };

  const handleDelete = async () => {
    const documentId = documentToDelete?.id;
    if (!documentId || downloadingId === documentId || deletingId === documentId) {
      return;
    }

    setActionError(null);
    setActionMessage(null);
    setDeletingId(documentId);

    try {
      await documentStorageService.deleteDocument(documentId, buildRequestOptions());
      await fetchDocuments({ showLoader: false });

      if (isMountedRef.current) {
        const deletedLabel = documentToDelete?.uiTypeLabel
          || documentToDelete?.title
          || documentToDelete?.originalFileName
          || 'Document';
        setActionMessage(`${deletedLabel} was deleted successfully.`);
        setDocumentToDelete(null);
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }

      const friendly = error instanceof ApiError
        ? error.getUserFriendlyMessage()
        : (error?.message || 'Failed to delete document.');
      setActionError(friendly);
    } finally {
      if (isMountedRef.current) {
        setDeletingId(null);
      }
    }
  };

  const handleDownload = async (doc) => {
    if (!doc || downloadingId === doc.id) {
      return;
    }

    setActionError(null);
    setActionMessage(null);
    setDownloadingId(doc.id);

    const filename = doc.originalFileName || doc.fileName || doc.title || `document-${doc.id}`;

    const downloadBlob = (blob) => {
      if (!blob) {
        throw new Error('Unable to download document.');
      }

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    };

    try {
      const response = await documentStorageService.downloadDocument(doc.id, buildRequestOptions());
      if (response?.data instanceof Blob) {
        downloadBlob(response.data);
      } else if (response?.data) {
        const contentType = response?.headers?.['content-type'] || 'application/octet-stream';
        const blob = response.data instanceof ArrayBuffer
          ? new Blob([response.data], { type: contentType })
          : new Blob([response.data], { type: contentType });
        downloadBlob(blob);
      } else {
        throw new Error('Document data not available.');
      }
    } catch (error) {
      const friendly = error instanceof ApiError
        ? error.getUserFriendlyMessage()
        : (error?.message || 'Failed to download document.');
      setActionError(friendly);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleView = async (doc) => {
    setActionError(null);
    setActionMessage(null);
    setViewerError(null);
    setViewerLoading(true);
    setViewerDocument(doc);

    try {
      if (doc.fileUrl) {
        setViewerUrl(doc.fileUrl);
        setViewerLoading(false);
        return;
      }

      const response = await documentStorageService.getDownloadUrl(doc.id, buildRequestOptions());
      const viewUrl = response?.download?.downloadUrl || response?.downloadUrl;
      if (!viewUrl) {
        throw new Error('View URL not available.');
      }
      setViewerUrl(viewUrl);
    } catch (error) {
      const friendly = error instanceof ApiError
        ? error.getUserFriendlyMessage()
        : (error?.message || 'Failed to open document for viewing.');
      setViewerError(friendly);
    } finally {
      setViewerLoading(false);
    }
  };

  const closeViewer = () => {
    setViewerDocument(null);
    setViewerUrl('');
    setViewerError(null);
    setViewerLoading(false);
  };

  const openViewerInNewTab = () => {
    const url = viewerUrl || viewerDocument?.fileUrl || '';
    if (!url) {
      setViewerError('Document URL is not available.');
      return;
    }
    window.open(url, '_blank', 'noopener');
  };

  const resolveViewerContent = () => {
    const url = viewerUrl || viewerDocument?.fileUrl || '';
    if (!url) {
      return null;
    }

    const mimeType = viewerDocument?.mimeType || '';
    const fileName = viewerDocument?.originalFileName || viewerDocument?.fileName || '';

    const isImage = () => {
      if (mimeType.startsWith('image/')) {
        return true;
      }
      return /\.(png|jpg|jpeg|gif|webp)$/i.test(fileName);
    };

    const isPdf = () => {
      if (mimeType === 'application/pdf') {
        return true;
      }
      return /\.pdf$/i.test(fileName);
    };

    if (isImage()) {
      return (
        <div className="flex justify-center">
          <img
            src={url}
            alt={fileName || 'Document preview'}
            className="max-h-[70vh] object-contain"
          />
        </div>
      );
    }

    const iframeUrl = isPdf() ? url : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

    return (
      <iframe
        title="Document viewer"
        src={iframeUrl}
        className="w-full h-[70vh] border"
      />
    );
  };

  const handleOpenUploadModal = () => {
    if (!driverId || !companyId) {
      setActionError('Driver and company context are required before uploading documents.');
      return;
    }

    setActionError(null);
    setActionMessage(null);
    resetUploadState();
    setShowUploadModal(true);
  };


  const renderStatusBadge = (doc) => {
    const status = doc.uiStatus || { text: 'Unknown', className: 'bg-gray-100 text-gray-700 border-gray-300' };
    return (
      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${status.className} shadow-sm sm:shadow-none`}>
        {status.text}
      </span>
    );
  };

  const toggleRowExpansion = (documentId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  };

  const expandAllRows = () => {
    setExpandedRows(new Set(filteredDocuments.map((doc) => doc.id)));
  };

  const collapseAllRows = () => {
    setExpandedRows(new Set());
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderSortIcon = (key) => {
    const baseClasses = 'h-5 w-5 transition-colors duration-150';

    if (sortConfig.key !== key) {
      return (
        <svg className={`${baseClasses} text-gray-300`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.3} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
        </svg>
      );
    }

    const activeClasses = `${baseClasses} text-blue-600`;

    if (sortConfig.direction === 'asc') {
      return (
        <svg className={activeClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.3} d="M8 15l4 4 4-4M8 9l4-4 4 4" />
        </svg>
      );
    }

    return (
      <svg className={activeClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.3} d="M8 9l4-4 4 4M8 15l4 4 4-4" />
      </svg>
    );
  };

  const hasDocuments = documents.length > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Document Management</h2>
        <p className="text-gray-600 mt-1">
          {driverName} - Driver ID: {driverId || 'N/A'}
        </p>
      </div>

      {actionError && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {actionError}
        </div>
      )}

      {actionMessage && (
        <div className="mb-4 rounded-md bg-green-50 border border-green-200 p-4 text-sm text-green-700">
          {actionMessage}
        </div>
      )}

      {loadError && (
        <div className="mb-4 rounded-md bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-800">
          {loadError}
        </div>
      )}

      {isLoading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-sm text-gray-500">
          Loading driver documents...
        </div>
      ) : (
        <>

          <div className="space-y-6">
            <div className="space-y-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1">
                    <div className="relative w-full sm:max-w-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        placeholder="Search documents..."
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="relative">
                      <select
                        value={selectedCategory}
                        onChange={(event) => setSelectedCategory(event.target.value)}
                        className="block w-full sm:w-auto px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium text-gray-700"
                      >
                        {documentCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name} ({category.count})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={handleOpenUploadModal}
                      className={`btn-primary px-4 ${(!driverId || !companyId) ? 'opacity-60 cursor-not-allowed' : ''}`}
                      disabled={!driverId || !companyId}
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Upload Document
                    </button>
                  </div>
                </div>

                {documents.length > 0 && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs sm:text-sm text-blue-800">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedStatus('all')}
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${
                        selectedStatus === 'all'
                          ? 'bg-blue-600 text-white border-blue-600 shadow'
                          : 'bg-white text-gray-700 border-blue-200 hover:bg-blue-100'
                      }`}
                    >
                      <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                      Total: {statusSummary.total}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedStatus('active')}
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${
                        selectedStatus === 'active'
                          ? 'bg-cyan-600 text-white border-cyan-600 shadow'
                          : 'bg-white text-cyan-700 border-cyan-200 hover:bg-cyan-100'
                      }`}
                    >
                      <span className="h-2.5 w-2.5 rounded-full bg-cyan-500" />
                      Active: {statusSummary.active}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedStatus('valid')}
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${
                        selectedStatus === 'valid'
                          ? 'bg-green-600 text-white border-green-600 shadow'
                          : 'bg-white text-green-700 border-green-200 hover:bg-green-100'
                      }`}
                    >
                      <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                      Valid: {statusSummary.valid}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedStatus('expiring')}
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${
                        selectedStatus === 'expiring'
                          ? 'bg-yellow-500 text-white border-yellow-500 shadow'
                          : 'bg-white text-yellow-600 border-yellow-200 hover:bg-yellow-100'
                      }`}
                    >
                      <span className="h-2.5 w-2.5 rounded-full bg-yellow-500" />
                      Expiring Soon: {statusSummary.expiring}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedStatus('expired')}
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${
                        selectedStatus === 'expired'
                          ? 'bg-red-600 text-white border-red-600 shadow'
                          : 'bg-white text-red-600 border-red-200 hover:bg-red-100'
                      }`}
                    >
                        <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
                        Expired: {statusSummary.expired}
                      </button>
                    </div>
                    <div
                      ref={statusInfoContainerRef}
                      className="relative flex items-center gap-2 text-blue-700"
                    >
                      <button
                        type="button"
                        onClick={() => setShowStatusInfo((prev) => !prev)}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full border border-blue-200 bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                          showStatusInfo ? 'bg-blue-100 text-blue-700' : 'hover:bg-blue-100'
                        }`}
                        aria-haspopup="dialog"
                        aria-expanded={showStatusInfo}
                        aria-label="Show document status definitions"
                      >
                        <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                        </svg>
                      </button>
                      {showStatusInfo && (
                        <div className="absolute right-0 top-full z-20 mt-2 w-72 rounded-lg border border-blue-200 bg-white p-4 text-left shadow-lg">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-semibold text-blue-900">Status definitions</p>
                            <button
                              type="button"
                              onClick={() => setShowStatusInfo(false)}
                              className="text-blue-500 transition hover:text-blue-700 focus:outline-none"
                              aria-label="Close status definitions"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            These labels are shared between the ribbon and the table so you always see a consistent status.
                          </p>
                          <ul className="mt-3 space-y-2 text-sm text-gray-700">
                            <li className="flex items-start gap-2">
                              <span className="mt-1 h-2.5 w-3 rounded-full bg-cyan-500" />
                              <div>
                                <p className="font-medium text-gray-900">Active</p>
                                <p className="text-xs text-gray-600">Document is currently usable. Includes anything without an expiration date and records marked Valid.</p>
                              </div>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="mt-1 h-2.5 w-3 rounded-full bg-green-500" />
                              <div>
                                <p className="font-medium text-gray-900">Valid</p>
                                <p className="text-xs text-gray-600">More than 60 days remain until expiration. This count is also included in Active.</p>
                              </div>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="mt-1 h-2.5 w-3 rounded-full bg-yellow-500" />
                              <div>
                                <p className="font-medium text-gray-900">Expiring Soon</p>
                                <p className="text-xs text-gray-600">Expires within the next 60 days, including any highlighted as Expiring Soon or Expires Soon in the table.</p>
                              </div>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="mt-1 h-2.5 w-3 rounded-full bg-red-500" />
                              <div>
                                <p className="font-medium text-gray-900">Expired</p>
                                <p className="text-xs text-gray-600">Past the expiration date. These documents require immediate attention.</p>
                              </div>
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {filteredDocuments.length > 0 ? (
                  <div className="bg-white shadow rounded-lg overflow-hidden">
                    <table className="min-w-full table-fixed divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-2/5">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleSort('uiTypeLabel')}
                                className={`inline-flex items-center gap-2 px-2 py-1 rounded-md font-semibold uppercase tracking-wider text-xs transition-colors ${
                                  sortConfig.key === 'uiTypeLabel'
                                    ? 'text-blue-600 bg-blue-50'
                                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                                }`}
                              >
                                Document
                                {renderSortIcon('uiTypeLabel')}
                              </button>
                              <button
                                type="button"
                                onClick={allExpanded ? collapseAllRows : expandAllRows}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-full border border-gray-300 text-gray-600 bg-white hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              >
                                <svg
                                  className={`h-3.5 w-3.5 transition-transform ${allExpanded ? 'rotate-180' : ''}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12h16m-8-8l8 8-8 8" />
                                </svg>
                                {allExpanded ? 'Collapse' : 'Expand'}
                              </button>
                            </div>
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-1/5">
                            <button
                              type="button"
                              onClick={() => handleSort('status')}
                              className={`inline-flex items-center gap-2 px-2 py-1 rounded-md font-semibold uppercase tracking-wider text-xs transition-colors ${
                                sortConfig.key === 'status'
                                  ? 'text-blue-600 bg-blue-50'
                                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                              }`}
                            >
                              Status
                              {renderSortIcon('status')}
                            </button>
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-1/6">
                            <button
                              type="button"
                              onClick={() => handleSort('expiration')}
                              className={`inline-flex items-center gap-2 px-2 py-1 rounded-md font-semibold uppercase tracking-wider text-xs transition-colors ${
                                sortConfig.key === 'expiration'
                                  ? 'text-blue-600 bg-blue-50'
                                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                              }`}
                            >
                              Expires
                              {renderSortIcon('expiration')}
                            </button>
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-1/6">
                            <button
                              type="button"
                              onClick={() => handleSort('upload')}
                              className={`inline-flex items-center gap-2 px-2 py-1 rounded-md font-semibold uppercase tracking-wider text-xs transition-colors ${
                                sortConfig.key === 'upload'
                                  ? 'text-blue-600 bg-blue-50'
                                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                              }`}
                            >
                              Uploaded
                              {renderSortIcon('upload')}
                            </button>
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredDocuments.map((doc) => {
                          const displayFileName = doc.originalFileName || doc.fileName || 'N/A';
                          const isExpanded = expandedRows.has(doc.id);
                          return (
                            <Fragment key={doc.id}>
                              <tr className={`hover:bg-gray-50 ${isExpanded ? 'bg-gray-50' : ''}`}>
                                <td className="px-4 py-3 align-top">
                                  <div className="flex items-start gap-3 min-w-0">
                                    <button
                                      type="button"
                                      onClick={() => toggleRowExpansion(doc.id)}
                                      className="mt-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                                      aria-label={isExpanded ? 'Collapse document details' : 'Expand document details'}
                                    >
                                      <svg
                                        className={`h-4 w-4 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                    </button>
                                    <div className="min-w-0 space-y-1">
                                      <p className="font-medium text-gray-900 truncate" title={doc.uiTypeLabel}>{doc.uiTypeLabel}</p>
                                      <p className="text-gray-500 text-xs break-words sm:truncate sm:break-normal" title={displayFileName}>{displayFileName}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 align-top">
                                  {renderStatusBadge(doc)}
                                </td>
                                <td className="px-4 py-3 align-top text-gray-900">
                                  {formatDate(doc.uiExpirationDate)}
                                </td>
                                <td className="px-4 py-3 align-top text-gray-900">
                                  {formatDate(doc.uiUploadDate)}
                                </td>
                                <td className="px-4 py-3 align-top">
                                  <div className="flex flex-col sm:flex-row sm:justify-end sm:items-center gap-2 sm:gap-2">
                                    <button
                                      onClick={() => handleView(doc)}
                                      className="inline-flex items-center justify-center h-9 w-9 border border-gray-300 rounded-full text-gray-600 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                      aria-label="View document"
                                    >
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0zm6 0c0 3-4.5 7-9 7s-9-4-9-7 4.5-7 9-7 9 4 9 7z" />
                                      </svg>
                                    </button>

                                    <button 
                                      onClick={() => handleDownload(doc)}
                                      className="text-gray-600 hover:text-gray-900" 
                                      title="Download Document"
                                      aria-label={downloadingId === doc.id ? 'Downloading document' : 'Download document'}
                                      disabled={downloadingId === doc.id}
                                    >

                                      {downloadingId !== doc.id ? (
                                        <Download className="h-4 w-4" />
                                      ):
                                        <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                        </svg>
                                      }
                                    </button>
                                    <button
                                      onClick={() => requestDeleteConfirmation(doc)}
                                      className="inline-flex items-center justify-center h-9 w-9 border border-gray-300 rounded-full text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                      aria-label="Delete document"
                                    >
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr className="bg-gray-50">
                                  <td colSpan={5} className="px-8 py-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-700">
                                      <div>
                                        <p className="text-xs uppercase text-gray-500">Uploaded By</p>
                                        <p className="mt-1 text-gray-900">{doc.uiUploadedBy || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs uppercase text-gray-500">File Size</p>
                                        <p className="mt-1 text-gray-900">{doc.uiFileSize || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs uppercase text-gray-500">Category</p>
                                        <p className="mt-1 text-gray-900">{CATEGORY_LABELS[doc.uiCategory] || toTitleCase(doc.uiCategory)}</p>
                                      </div>
                                      <div className="sm:col-span-2 lg:col-span-3">
                                        <p className="text-xs uppercase text-gray-500">File Name</p>
                                        <p className="mt-1 text-gray-900 break-words">{displayFileName}</p>
                                      </div>
                                      {doc.notes && (
                                        <div className="sm:col-span-2 lg:col-span-3">
                                          <p className="text-xs uppercase text-gray-500">Notes</p>
                                          <p className="mt-1 text-gray-800 whitespace-pre-wrap">{doc.notes}</p>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                <div className="bg-white border border-dashed border-gray-200 rounded-lg p-12 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No documents found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {hasDocuments ? `No documents match "${searchTerm}"` : 'No documents have been uploaded for this driver yet.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 px-4">
          <div className="relative top-10 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <form onSubmit={handleUpload}>
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Document</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Document Type *
                    </label>
                    <select
                      value={uploadForm.documentType}
                      onChange={(event) => handleDocumentTypeChange(event.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select document type</option>
                      {CATEGORY_ORDER.filter((category) => category !== 'all').map((category) => (
                        groupedDocumentTypes[category]?.length ? (
                          <optgroup key={category} label={CATEGORY_LABELS[category] || toTitleCase(category)}>
                            {groupedDocumentTypes[category].map((type) => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </optgroup>
                        ) : null
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={uploadForm.title}
                      onChange={handleTitleChange}
                      placeholder="e.g., CDL License 2024"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Issue Date
                      </label>
                      <input
                        type="date"
                        value={uploadForm.issueDate}
                        onChange={(event) => setUploadForm((prev) => ({ ...prev, issueDate: event.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expiration Date
                      </label>
                      <input
                        type="date"
                        value={uploadForm.expirationDate}
                        onChange={(event) => setUploadForm((prev) => ({ ...prev, expirationDate: event.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={uploadForm.notes}
                      onChange={(event) => setUploadForm((prev) => ({ ...prev, notes: event.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Additional notes about this document..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      File *
                    </label>
                    <div
                      className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
                        dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <div className="space-y-1 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500">
                            <span>Upload a file</span>
                            <input
                              id="file-upload"
                              ref={fileInputRef}
                              type="file"
                              className="sr-only"
                              onChange={(event) => handleFileSelect(event.target.files)}
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          PDF, DOC, DOCX, XLS, XLSX, JPG, PNG up to 10MB
                        </p>
                        {uploadForm.file && (
                          <p className="text-sm text-green-600 font-medium">
                            Selected: {uploadForm.file.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {uploadError && (
                    <p className="text-sm text-red-600">{uploadError}</p>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  type="button"
                  onClick={closeUploadModal}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn-primary w-full sm:w-auto ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {documentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4 py-6">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg p-4 sm:p-6">
            <button
              type="button"
              onClick={() => setDocumentToDelete(null)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              aria-label="Close delete confirmation"
              disabled={deletingId === documentToDelete.id}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete document?</h3>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete
              {' '}
              <span className="font-medium text-gray-900">
                {documentToDelete.uiTypeLabel || documentToDelete.title || documentToDelete.originalFileName || 'this document'}
              </span>
              ? This action permanently removes the file from storage.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row sm:justify-end sm:space-x-3 space-y-3 sm:space-y-0">
              <button
                type="button"
                onClick={() => setDocumentToDelete(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={deletingId === documentToDelete.id}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                  deletingId === documentToDelete.id ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                }`}
                disabled={deletingId === documentToDelete.id}
              >
                {deletingId === documentToDelete.id ? (
                  <span className="flex items-center">
                    <svg className="animate-spin h-4 w-4 mr-2 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Deleting...
                  </span>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewerDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4 py-6">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl p-4 sm:p-6">
            <button
              type="button"
              onClick={closeViewer}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              aria-label="Close viewer"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{viewerDocument.uiTypeLabel || viewerDocument.title}</h3>
              <p className="text-sm text-gray-500">
                {viewerDocument.originalFileName || viewerDocument.fileName || 'Document'}
              </p>
            </div>

            <div className="bg-gray-100 rounded-md p-4">
              {viewerLoading && (
                <div className="text-center text-gray-600 text-sm">Preparing viewer...</div>
              )}

              {!viewerLoading && viewerError && (
                <div className="text-center text-red-600 text-sm">{viewerError}</div>
              )}

              {!viewerLoading && !viewerError && resolveViewerContent()}
            </div>

            <div className="mt-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={openViewerInNewTab}
                className={`px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  viewerLoading || !(viewerUrl || viewerDocument?.fileUrl) ? 'opacity-60 cursor-not-allowed' : ''
                }`}
                disabled={viewerLoading || !(viewerUrl || viewerDocument?.fileUrl)}
              >
                Open in New Tab
              </button>
              <button
                type="button"
                onClick={closeViewer}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverDocuments;
